import { type JobsApi } from '@/features/jobs/api/job-api'
import { collection } from '@/infra/collection'
import { Insight } from './Insight'
import { Id } from '@/infra/id'
import {
  GetLatestJobInsightsRequest,
  GenerateJobInsightsRequest,
} from '@taxfyle/api-internal/internal/insights_pb'
import { mapInsightProtoToProps } from './insight-mappers'

export class InsightStore {
  /**
   * Insights for jobs.
   */
  readonly jobInsights = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Insight(),
  })

  constructor(private readonly _api: JobsApi) {}

  /**
   * Get all the latest insights in the collection for the given job ID.
   */
  latestInsightsForJob(jobId: Id) {
    return this.jobInsights.filter((i) => i.jobId === jobId)
  }

  /**
   * Fetches the latest Insights for the given job ID.
   */
  async fetchLatestJobInsights(jobId: Id, signal?: AbortSignal): Promise<void> {
    const request = new GetLatestJobInsightsRequest()
    request.setJobId(Id.assert(jobId))

    const res = await this._api.insights.getLatestJobInsights(request)
    signal?.throwIfAborted()

    this._clearJobInsightsForJobId(jobId)
    this.jobInsights.set(
      res
        .getInsightsList()
        .map((insight) => mapInsightProtoToProps(jobId, insight)),
    )
  }

  /**
   * Generate all Insights or refresh all Insights for the given job ID.
   */
  async generateOrRefreshJobInsights(
    jobId: Id,
    signal?: AbortSignal,
  ): Promise<void> {
    const request = new GenerateJobInsightsRequest()
    request.setJobId(Id.assert(jobId))
    // Currently we only offer 5 insights in total, so lets just return all of them.
    request.setPageSize(5)

    await this._api.insights.generateJobInsights(request)

    await this.fetchLatestJobInsights(jobId, signal)
  }

  private _clearJobInsightsForJobId(jobId: Id): void {
    this.jobInsights
      .filter((i) => i.jobId === jobId)
      .forEach((insight) => {
        this.jobInsights.remove(insight)
      })
  }
}
